import { Typography, Box, Link, Button, Icon } from '@ace-clublabs/shared-component-library';
import { AlreadyPaidOptions } from '../shared/types';
import { AppFlow } from '@lib/web-shared';
import { useGlobalState } from '../shared/state';
import { constants } from '@ace-clublabs/payment-types';
import { getTermsUrl } from '../utils/methods';

interface Props {
  onEnrollAutoPay: () => void;
  onSkipAutoPay: () => void;
  onCancel: () => void;
  minimumDue?: number;
  fullBalance?: number;
  alreadyPaidOption?: AlreadyPaidOptions;
  allowAutoPay?: boolean;
  appFlow: AppFlow;
}

export const AutoPayForm: React.FC<Props> = ({
  onEnrollAutoPay,
  onSkipAutoPay,
  onCancel,
  minimumDue,
  fullBalance,
  alreadyPaidOption,
  allowAutoPay,
  appFlow,
}) => {
  const [clubCode] = useGlobalState('clubCode');

  const noBalanceAutoPay = !minimumDue && !fullBalance && allowAutoPay;
  const hasBalanceChosenAutoPayOnly = alreadyPaidOption === AlreadyPaidOptions.EnrollInAutoPay;

  const renderHeaderMessage = () => {
    if (appFlow === AppFlow.AUTOPAY || hasBalanceChosenAutoPayOnly) return 'Confirm your Auto Pay enrollment';
    else if (noBalanceAutoPay) return 'You have no payment due. Would you like to set up Auto Pay for future payments?';
    else return 'Would you like to enroll in Auto Pay?';
  };

  const renderAutoPayMessageBody = () => {
    return (
      <>
        <Typography variant="b1Regular" component="h2" id="autopay-description-p1" className="margin8Top">
          Never miss a payment with Auto Pay.
        </Typography>
        <Box id="autopay-benefits-box" className="margin16Top">
          <Box id="autopay-benefits-header" className="margin8Bottom">
            <Icon iconName="circle-dollar-duotone" className="autopay-benefits-icon" />
            <Typography variant="b1Medium">Auto Pay benefits</Typography>
          </Box>
          <Box id="autopay-benefits-list">
            <Box className="autopay-benefits-item">
              <Typography className="bullet">•</Typography>
              <Typography variant="b1Regular">Enjoy automatic insurance renewal</Typography>
            </Box>
            <Box className="autopay-benefits-item">
              <Typography className="bullet">•</Typography>
              <Typography variant="b1Regular">
                Avoid a lapse in coverage with payments that are always on time
              </Typography>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  const renderAutoPayActions = () => {
    const isAutoPayFlow = appFlow === AppFlow.AUTOPAY;

    const renderCancelButton = () => (
      <Button variant="outlined" id="cancel-button" onClick={onCancel} label="Cancel" fullWidth sx={{ mt: 1 }} />
    );

    const renderSkipAndPayButton = () => (
      <Button
        variant="outlined"
        id="skip-and-pay-button"
        onClick={onSkipAutoPay}
        label="Skip and pay"
        fullWidth
        sx={{ mt: 1 }}
      />
    );

    return (
      <>
        <Button
          variant="contained"
          id="enroll-continue-button"
          onClick={onEnrollAutoPay}
          label="Enroll and continue"
          fullWidth
          sx={{ mt: 3 }}
        />
        {!isAutoPayFlow && noBalanceAutoPay
          ? renderCancelButton()
          : !isAutoPayFlow && !hasBalanceChosenAutoPayOnly
            ? renderSkipAndPayButton()
            : null}
      </>
    );
  };

  const renderAutoPayFooter = () => {
    return (
      <Box className="margin16Top">
        <Typography variant="b2Regular" component="span" id="autopay-legal">
          By clicking Enroll and continue, I hereby enroll in Auto Pay and have read and agree to the{' '}
          <Link id="terms-and-conditions-link" target="_blank" href={getTermsUrl()}>
            Terms & Conditions
          </Link>
          .
        </Typography>
      </Box>
    );
  };

  const renderDisclaimer = () => {
    return clubCode === '004' ? (
      <>
        <Typography
          component="h3"
          variant="b2Regular"
          sx={{ mt: 2, color: '#6C7175' }}
          id="ca-finance-fee-disclaimer"
          className="margin8Top"
        >
          {constants['disclaimer_finance_fee_title']}
        </Typography>

        <Typography
          component="h3"
          variant="b2Regular"
          id="ca-finance-fee-disclaimer-body"
          className="margin8Top"
          sx={{ color: '#6C7175' }}
        >
          {constants['disclaimer_finance_fee']}
        </Typography>
      </>
    ) : (
      <></>
    );
  };

  return (
    <Box id="autopay-form" className="margin16Bottom">
      <Typography variant="h5Medium" component="h1" id="headline" sx={{}}>
        {renderHeaderMessage()}
      </Typography>
      {renderAutoPayMessageBody()}
      {renderAutoPayActions()}
      {renderAutoPayFooter()}
      {renderDisclaimer()}
    </Box>
  );
};
